import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import BottomNav from "@/components/BottomNav";
import { ArrowLeft, Plus, Trash2, Star } from "lucide-react";

/**
 * Legacy standalone personas list page. Still reachable from Library ("Manage"
 * links). Uses the full-page editor at /create-persona and /edit-persona/:id
 * — no more modal.
 */
export default function Personas() {
  const nav = useNavigate();
  const [personas, setPersonas] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    api.get("/personas").then(({ data }) => setPersonas(data.personas)).finally(() => setLoading(false));
  };
  useEffect(load, []);

  async function del(id) {
    if (!confirm("Delete this persona?")) return;
    await api.delete(`/personas/${id}`);
    load();
  }

  return (
    <div className="mx-auto max-w-2xl px-4 pb-32 pt-safe-6">
      <header className="mb-6 flex items-center gap-3">
        <button data-testid="personas-back-btn" onClick={() => nav(-1)} className="flex h-10 w-10 items-center justify-center rounded-full bg-white/5 text-white/70 hover:bg-white/10">
          <ArrowLeft size={18} />
        </button>
        <div className="flex-1">
          <div className="font-display text-xs uppercase tracking-[0.35em] text-violet-400/80">Your</div>
          <h1 className="font-display text-3xl leading-tight text-white">Personas</h1>
        </div>
        <button
          data-testid="personas-new-btn"
          onClick={() => nav("/create-persona")}
          className="btn-pill btn-primary px-4 py-2"
        >
          <Plus size={16} /> New
        </button>
      </header>

      <p className="mb-5 text-sm text-neutral-400">
        A persona is who <em>you</em> are in the story. Pick one when starting a chat, or switch mid-story.
      </p>

      {loading ? (
        <div className="space-y-2">
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="h-20 animate-pulse rounded-2xl bg-white/[0.03]" />
          ))}
        </div>
      ) : personas.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-white/10 p-8 text-center" data-testid="personas-empty">
          <div className="font-display text-xl text-white">No personas yet</div>
          <p className="mt-2 text-sm text-neutral-500">Create your first persona to roleplay as a distinct character.</p>
        </div>
      ) : (
        <ul className="space-y-2" data-testid="personas-list">
          {personas.map((p) => (
            <li key={p.id} className="flex items-start gap-3 rounded-2xl border border-white/5 bg-white/[0.02] p-3">
              {p.avatar_url ? (
                <img src={p.avatar_url} alt="" className="h-14 w-14 rounded-xl object-cover" />
              ) : (
                <div className="flex h-14 w-14 items-center justify-center rounded-xl bg-fuchsia-500/20 font-display text-xl text-fuchsia-200">
                  {p.name[0]}
                </div>
              )}
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <span className="font-display text-lg text-white truncate">{p.name}</span>
                  {p.is_default && <Star size={12} className="text-yellow-400 fill-yellow-400" />}
                </div>
                <p className="text-xs text-neutral-400 line-clamp-2">{p.description}</p>
              </div>
              <div className="flex gap-1">
                <button
                  onClick={() => nav(`/edit-persona/${p.id}`)}
                  data-testid={`persona-edit-${p.id}`}
                  className="rounded-lg bg-white/5 px-3 py-1.5 text-xs text-white/80 hover:bg-white/10"
                >
                  Edit
                </button>
                <button
                  onClick={() => del(p.id)}
                  data-testid={`persona-delete-${p.id}`}
                  className="rounded-lg bg-white/5 p-1.5 text-neutral-400 hover:bg-white/10 hover:text-rose-300"
                  aria-label="Delete persona"
                >
                  <Trash2 size={14} />
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}

      <BottomNav />
    </div>
  );
}
